import { createReactElement, UpdatableReactElement } from 'shared/utils/react.utils'
import { BaseModalController, IModal, ModalContext } from '../Modal'
import { DecisionStatusType, TaskMode, NotificationPropsType } from 'Types/Tasks.types'
import { RejectOrSkipModal } from 'Components/modal/RejectOrSkipModal/RejectOrSkipModal'
import { TaskApiErrorResponse, cancelTaskSubmission } from 'Services/tasks.service'
import { ErrorCode } from 'Constants/constants'
import type { CSATFeedbackConfig, CSATFeedbackData } from 'Types/CSAT.types'

export type RejectOrSkipModalMetaData = {
    isSkip: boolean
    taskMode?: TaskMode
    skipReasons?: string[]
    rejectionReasons?: string[]
    isSkipReasonSelectionOptional?: boolean
    isRejectionReasonSelectionOptional?: boolean
    feedbackConfig?: CSATFeedbackConfig
}

export class RejectOrSkipModalController
    extends BaseModalController<RejectOrSkipModalMetaData>
    implements IModal<RejectOrSkipModalMetaData>
{
    static Id = 'reject-or-skip-modal' as const
    constructor(modalContext: ModalContext) {
        super(modalContext, RejectOrSkipModalController.Id)
        this.metaData = { isSkip: true }
    }

    createElement(): UpdatableReactElement {
        const onConfirm = (
            action: 'submit' | 'dismiss' = 'dismiss',
            payload?: { comment: string; skipReasons?: string[]; rejectionReasons?: string[] }
        ) => {
            this.onClose(action, payload)
        }
        const onCancel = () => {
            this.onClose('dismiss')
        }
        return createReactElement(RejectOrSkipModal, {
            getVisibility: this.getVisibility.bind(this),
            getMetaData: this.getMetaData.bind(this) as () => RejectOrSkipModalMetaData,
            onConfirm,
            onCancel,
        })
    }

    private onClose = (
        action: 'submit' | 'dismiss' = 'dismiss',
        payload?: {
            comment: string
            skipReasons?: string[]
            rejectionReasons?: string[]
            feedbackData?: CSATFeedbackData
        }
    ) => {
        const handleClose = async () => {
            const draftContainer = this.modalContext.task.taskState.getDraftOutputContainer()
            if (action === 'submit' && this.metaData?.isSkip) {
                try {
                    await cancelTaskSubmission(
                        this.modalContext.task.taskState?.getCurrentState()?.submission.id ?? '',
                        payload?.comment ?? '',
                        payload?.skipReasons,
                        payload?.feedbackData
                    )
                    this.modalContext.task.navigateTask({
                        type: 'success',
                        message: 'Task skipped successfully',
                    })
                } catch (e) {
                    const error = e as TaskApiErrorResponse
                    const errorCode = error.response.data.error.code
                    let errorMessage = error.response.data.error.message

                    if (
                        errorCode === ErrorCode.SKIP_REASONS_CHANGED ||
                        errorCode === ErrorCode.REJECTION_REASONS_CHANGED
                    ) {
                        errorMessage = `${errorMessage}Please refresh the page`
                    }

                    this.modalContext.task.showNotification(errorMessage, 'error')
                    if ((e as Error).message === 'Task submission already cancelled by admin.') {
                        this.modalContext.task.navigateTask({
                            type: 'error',
                            message: (e as Error).message,
                        })
                    }
                }
            } else if (action === 'submit' && !this.metaData?.isSkip && draftContainer) {
                const stepName = this.modalContext.studio.taskConfig.assignment.step?.name ?? ''
                this.modalContext.task.taskState.createDecision(
                    stepName,
                    payload?.comment ?? '',
                    DecisionStatusType.REJECT,
                    payload?.rejectionReasons
                )

                await this.modalContext.task.submitTask({
                    onComplete: (notifications?: NotificationPropsType) => {
                        if (notifications?.type === 'success') {
                            this.modalContext.task.navigateTask(notifications)
                        } else {
                            this.modalContext.task.taskState.setDecision(null)
                        }
                    },
                })
            }
            this.setVisibility(false)
        }

        handleClose()
    }
}
